// `device_add` properties shared by every add path that commits a camera:
// single add, bulk add, and the ONVIF placeholder a bulk add leaves for the
// user to sign in to later. Kept out of wiring.ts so auth-dispatch.ts can use
// it without importing the add-flow hooks.
import { ONBOARDING_ADD_METHOD } from '#~/analytics/constants/onboarding-events.js';
import {
  resolveDeviceAnalyticsCatalogContextFromModel,
  type DeviceAnalyticsCatalogModelLike
} from '#~/analytics/device-catalog.js';
import type { DeviceAddSuccessProperties } from '#~/analytics/domains/device-lifecycle.js';
import type { Minimist } from '#~/device/index.js';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';

/** Stream settings `device_add` reports for a committed camera's video. */
export const resolveVideoStreamProps = (
  video: T_Maybe<object>
): Pick<DeviceAddSuccessProperties, 'bitrate' | 'resolution' | 'fps'> => {
  const videoQuery =
    video && 'query' in video ? (video.query as Record<string, unknown> | undefined) : undefined;
  if (!videoQuery) {
    return {};
  }
  const protocol =
    video && 'protocol' in video && video.protocol != null ? String(video.protocol) : undefined;

  if (protocol === 'local') {
    const width = videoQuery.width ? Number(videoQuery.width) : undefined;
    const height = videoQuery.height ? Number(videoQuery.height) : undefined;
    const rawFps = videoQuery.framerate ? String(videoQuery.framerate) : undefined;
    const fps = rawFps?.includes('/')
      ? Number(rawFps.split('/')[0]) / Number(rawFps.split('/')[1])
      : rawFps
        ? Number(rawFps)
        : undefined;
    const bitrate = videoQuery.bitrate ? Number(videoQuery.bitrate) : undefined;
    const resolution = width && height ? `${width}x${height}` : undefined;

    return {
      ...(bitrate ? { bitrate } : {}),
      ...(resolution ? { resolution } : {}),
      ...(fps ? { fps } : {})
    };
  }

  if (protocol === 'ndi') {
    const height = videoQuery.height ? Number(videoQuery.height) : undefined;
    const fps = videoQuery.ndiFramerate ? Number(videoQuery.ndiFramerate) : undefined;
    const bitrate = videoQuery.ndiBitrate ? Number(videoQuery.ndiBitrate) : undefined;
    const resolution = height ? `${height}p` : undefined;

    return {
      ...(bitrate ? { bitrate } : {}),
      ...(resolution ? { resolution } : {}),
      ...(fps ? { fps } : {})
    };
  }

  return {};
};

/**
 * `device_add` properties for a camera committed from the discovery list
 * without the single-add wizard (bulk add, and the ONVIF placeholder it
 * leaves for sign-in). Matches what single add sends for a discovered camera
 * (ENG-4950).
 *
 * @param device - The device data that was committed.
 * @param roomId - Studio the camera was added to.
 * @param model - Registry model of the matched template, when there is one.
 * @param fingerprintMatched - Whether the camera's own fingerprint named its
 *   model. False for a brand-only camera saved with the brand's generic model,
 *   which must not be reported as a backend model match.
 */
export const discoveryDeviceAddProperties = (
  device: Pick<Minimist, 'id' | 'modelId' | 'video'>,
  roomId: T_Maybe<Room['id']>,
  model?: T_Maybe<DeviceAnalyticsCatalogModelLike>,
  fingerprintMatched: boolean = Boolean(device.modelId)
): DeviceAddSuccessProperties => {
  const catalog = resolveDeviceAnalyticsCatalogContextFromModel(model, device.modelId);
  const protocol = device.video?.protocol?.toString();
  return {
    brand_name: catalog.brand_name ?? '',
    model_name: catalog.model_name ?? '',
    device_brand_id: catalog.device_brand_id ?? '',
    device_model_id: catalog.device_model_id ?? '',
    // There is no manual model picker here: a model the camera's fingerprint
    // named is what single add reports as one_click.
    ...(device.modelId && fingerprintMatched
      ? { add_mode: 'one_click' as const, model_id_source: 'backend' as const }
      : {}),
    operation: 'device-save',
    device_id: device.id,
    ...(roomId ? { studio_id: roomId.toString() } : {}),
    deviceType: protocol ?? '',
    ...(protocol ? { protocol } : {}),
    add_method: ONBOARDING_ADD_METHOD.DISCOVERY,
    ...resolveVideoStreamProps(device.video)
  };
};
